import React, { useState, useEffect } from 'react';
import { Users, Settings, BookOpen, Vote, Shield, UserMinus, CheckCircle2, XCircle, ExternalLink, MessageSquare, Plus, Loader2, Info, Sparkles, Flag } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import ReadingHeatmap from './ReadingHeatmap';
import SanctuaryFavorites from './SanctuaryFavorites';

// None of the group tables below have a foreign key to `public.profiles` — their
// `user_id` columns reference `auth.users(id)` instead — so asking PostgREST to embed
// `profiles(...)` fails the whole query with PGRST200. Resolve the profile rows with a
// second lookup and attach them under the same `profiles` key the UI already reads.
const attachProfiles = async (rows) => {
  const list = rows || [];
  const ids = [...new Set(list.map(r => r.user_id).filter(Boolean))];
  if (ids.length === 0) return list;

  const { data, error } = await supabase
    .from('profiles')
    .select('id, username, avatar_url')
    .in('id', ids);

  if (error) {
    console.error('Error fetching profiles for group rows:', error);
    return list;
  }

  const byId = new Map((data || []).map(p => [p.id, p]));
  return list.map(r => ({ ...r, profiles: byId.get(r.user_id) || null }));
};

const GroupHub = ({ groupId, onBack }) => {
  const { user, profile } = useAuth();
  const [group, setGroup] = useState(null);
  const [role, setRole] = useState('member');
  const [members, setMembers] = useState([]);
  const [polls, setPolls] = useState([]);
  const [lists, setLists] = useState([]);
  const [heatmap, setHeatmap] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'chat', 'lists', 'polls', 'members', 'buddies', 'settings', 'reports'
  const [selectedBuddySessionId, setSelectedBuddySessionId] = useState(null);
  const [buddySessions, setBuddySessions] = useState([]);
  const [pruneDays, setPruneDays] = useState(30);
  
  // Creation State
  const [isPollModalOpen, setIsPollModalOpen] = useState(false);
  const [isListModalOpen, setIsListModalOpen] = useState(false);

  // Admin Data
  const [applications, setApplications] = useState([]);

  useEffect(() => {
    fetchGroupData();
  }, [groupId]);

  const hasPermission = (permKey) => {
    if (profile?.is_system_admin) return true;
    if (role === 'owner') return true;
    const permRoles = group?.permissions?.[permKey];
    if (Array.isArray(permRoles)) {
      return permRoles.includes(role);
    }
    const defaults = {
      can_post_frozen_chat: ['owner', 'admin', 'moderator'],
      can_approve_applications: ['owner', 'admin', 'moderator'],
      can_prune_members: ['owner', 'admin'],
      can_change_settings: ['owner', 'admin'],
      can_kick_members: ['owner', 'admin', 'moderator'],
      can_delete_messages: ['owner', 'admin', 'moderator'],
      can_view_reports: ['owner', 'admin', 'moderator']
    };
    return (defaults[permKey] || []).includes(role);
  };

  const handlePrune = async () => {
    const confirmMsg = `Are you sure you want to prune all regular members who have been inactive in this circle for more than ${pruneDays} days?\n\nThis cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;

    try {
      const { data, error } = await supabase.rpc('prune_inactive_members', {
        target_group_id: groupId,
        threshold_days: pruneDays
      });

      if (error) throw error;
      alert(`🍂 Roster pruned successfully! Removed ${data} inactive member(s).`);
      fetchGroupData();
    } catch (err) {
      alert("Error pruning members: " + err.message);
    }
  };

  const fetchGroupData = async () => {
    setLoading(true);
    try {
      // 1. Group Core & My Role
      const [groupRes, roleRes] = await Promise.all([
        supabase.from('book_groups').select('*').eq('id', groupId).single(),
        supabase.rpc('get_my_group_role', { target_group_id: groupId })
      ]);

      if (groupRes.error) throw groupRes.error;
      setGroup(groupRes.data);
      setRole(roleRes.data || 'member');

      // 2. Heatmap
      const { data: hm } = await supabase.rpc('get_group_heatmap', { target_group_id: groupId });
      setHeatmap(hm || []);

      // 3. Members
      const { data: mems } = await supabase
        .from('members')
        .select('*')
        .eq('group_id', groupId);
      setMembers(mems || []);

      // 4. Polls
      const { data: plls } = await supabase
        .from('group_polls')
        .select('*, group_poll_options(*)')
        .eq('group_id', groupId)
        .order('created_at', { ascending: false });
      setPolls(plls || []);

      // 5. Lists
      const { data: lsts } = await supabase
        .from('group_lists')
        // book_id has to be selected explicitly — the buddy-session fetch below keys off it,
        // and without it the filter went out as `book_id=in.(undefined)` and 400'd.
        .select('*, group_list_items(book_id, books(*))')
        .eq('group_id', groupId);
      setLists(lsts || []);

      // Get all book IDs on these lists
      const bookIds = (lsts || []).flatMap(l => (l.group_list_items || []).map(item => item.book_id));

      if (bookIds.length > 0) {
        // Fetch buddy sessions for these books
        const { data: bs } = await supabase
          .from('buddy_sessions')
          .select('*, books(*), buddy_participants(*)')
          .in('book_id', bookIds)
          .eq('status', 'active');

        // Resolve every session's participants in one profiles lookup, then re-nest them.
        const sessions = bs || [];
        const allParticipants = await attachProfiles(
          sessions.flatMap(s => s.buddy_participants || [])
        );
        // Key by the real primary key (session_id, user_id) - a user can be in more than one
        // active session in the same group, and a user_id-only key let one row overwrite the other.
        const participantByKey = new Map(allParticipants.map(p => [`${p.session_id}:${p.user_id}`, p]));
        setBuddySessions(sessions.map(s => ({
          ...s,
          buddy_participants: (s.buddy_participants || []).map(
            p => participantByKey.get(`${p.session_id}:${p.user_id}`) || p
          )
        })));
      } else {
        setBuddySessions([]);
      }

      // 6. Admin Data
      const curRole = roleRes.data || 'member';
      const isSystemAdmin = profile?.is_system_admin || false;
      const parsedPermissions = groupRes.data.permissions || {};
      const canViewReps = curRole === 'owner' || isSystemAdmin || (parsedPermissions.can_view_reports || ['owner', 'admin', 'moderator']).includes(curRole);

      if (['owner', 'admin', 'moderator'].includes(curRole)) {
        const { data: apps } = await supabase
          .from('group_applications')
          .select('*')
          .eq('group_id', groupId)
          .eq('status', 'pending');
        setApplications(await attachProfiles(apps));
      }

      // 7. Moderation Reports
      if (canViewReps) {
        const { data: reps } = await supabase
          .from('group_reports')
          .select('*')
          .eq('group_id', groupId)
          .order('created_at', { ascending: false });
        setReports(reps || []);
      } else {
        setReports([]);
      }

    } catch (err) {
      console.error("Failed to fetch group hub data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (pollId, optionId) => {
    const { error } = await supabase.rpc('cast_group_vote', { 
      target_poll_id: pollId, 
      target_option_id: optionId 
    });
    if (error) alert(error.message);
    else fetchGroupData();
  };

  const handleApplication = async (appId, status) => {
    const { error } = await supabase.rpc('handle_group_application', { 
      app_id: appId, 
      new_status: status 
    });
    if (error) alert(error.message);
    else fetchGroupData();
  };

  const handleLeaveGroup = async () => {
    const isOwner = role === 'owner';
    let successorId = null;

    if (isOwner && members.length > 1) {
      const otherMembers = members.filter(m => m.user_id !== user.id);
      const choice = window.prompt(
        `As the owner, you must transfer leadership before leaving.\n\nType the USERNAME of your successor:\n${otherMembers.map(m => m.username).join(', ')}`
      );
      
      const found = otherMembers.find(m => m.username === choice);
      if (!found) return alert("Invalid successor username selected.");
      successorId = found.user_id;
    } else {
      if (!window.confirm("Are you sure you want to leave this sanctuary?")) return;
    }

    const { error } = await supabase.rpc('leave_or_archive_group', { 
      target_group_id: groupId, 
      successor_id: successorId 
    });

    if (error) alert(error.message);
    else onBack();
  };

  const handleArchiveGroup = async () => {
    if (!window.confirm("Archiving this group will hide it from the public. Members can still view it. Proceed?")) return;
    const { error } = await supabase.rpc('archive_group', { target_group_id: groupId });
    if (error) alert(error.message);
    else onBack();
  };

  const handleStartBuddySession = async (bookId) => {
    try {
      // 1. Create Session
      const { data: session, error: sErr } = await supabase
        .from('buddy_sessions')
        .insert([{ book_id: bookId }])
        .select()
        .single();
      
      if (sErr) throw sErr;

      // 2. Join as Participant
      const { error: pErr } = await supabase
        .from('buddy_participants')
        .insert([{
          session_id: session.id,
          user_id: user.id,
          current_page: 0,
          is_ready: true
        }]);

      if (pErr) throw pErr;

      window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: 'Buddy session established! 📖', type: 'success' } }));
      fetchGroupData();
      setActiveTab('buddies');
      setSelectedBuddySessionId(session.id);
    } catch (err) {
      window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: err.message, type: 'error' } }));
    }
  };

  const createPoll = async (title, options) => {
    const { data: poll, error: pErr } = await supabase
      .from('group_polls')
      .insert([{ group_id: groupId, title, created_by: user.id }])
      .select()
      .single();
    
    if (pErr) return alert(pErr.message);

    const opts = options.map(opt => ({ poll_id: poll.id, title: opt }));
    await supabase.from('group_poll_options').insert(opts);
    
    setIsPollModalOpen(false);
    fetchGroupData();
  };

  const createList = async (name) => {
    const { error } = await supabase
      .from('group_lists')
      .insert([{ group_id: groupId, name }]);
    
    if (error) alert(error.message);
    else {
      setIsListModalOpen(false);
      fetchGroupData();
    }
  };

  if (loading) return <div style={msg}><Loader2 className="spin" /> Entering the sanctuary hub...</div>;
  if (!group) return <div style={msg}>Group not found.</div>;

  const roleNames = group.role_names || {};

  const activeSession = buddySessions.find(s => s.id === selectedBuddySessionId);

  if (selectedBuddySessionId && activeSession) {
    return (
      <div className="fade-in">
        <BuddySessionHub 
          session={activeSession}
          onBack={() => {
            setSelectedBuddySessionId(null);
            fetchGroupData();
          }}
        />
      </div>
    );
  }

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={header}>
        <div>
          <h2 style={{ margin: 0, color: 'var(--text-main)' }}>{group.name}</h2>
          <div style={badge}>{roleNames[role] || role}</div>
        </div>
        <div style={headerActions}>
          {group.discord_server_url && (
            <a href={group.discord_server_url} target="_blank" rel="noopener noreferrer" style={discordBtn}>
              <MessageSquare size={16} /> Discord
            </a>
          )}
          <button onClick={onBack} style={backBtn}>Exit Hub</button>
        </div>
      </div>

      {/* Nav */}
      <div style={nav}>
        <button onClick={() => setActiveTab('overview')} style={activeTab === 'overview' ? activeTabBtn : tabBtn}>Overview</button>
        <button onClick={() => setActiveTab('chat')} style={activeTab === 'chat' ? activeTabBtn : tabBtn}>Board 💬</button>
        <button onClick={() => setActiveTab('lists')} style={activeTab === 'lists' ? activeTabBtn : tabBtn}>Shelves ({lists.length})</button>
        <button onClick={() => setActiveTab('polls')} style={activeTab === 'polls' ? activeTabBtn : tabBtn}>Polls ({polls.length})</button>
        <button onClick={() => setActiveTab('members')} style={activeTab === 'members' ? activeTabBtn : tabBtn}>Members ({members.length})</button>
        <button onClick={() => setActiveTab('favorites')} style={activeTab === 'favorites' ? activeTabBtn : tabBtn}>Favorites</button>
        <button onClick={() => setActiveTab('buddies')} style={activeTab === 'buddies' ? activeTabBtn : tabBtn}>Buddies ({buddySessions.length})</button>
        {hasPermission('can_change_settings') && (
          <button onClick={() => setActiveTab('settings')} style={activeTab === 'settings' ? activeTabBtn : tabBtn}>
            Settings {applications.length > 0 && <span style={notifBadge}>{applications.length}</span>}
          </button>
        )}
        {hasPermission('can_view_reports') && (
          <button onClick={() => setActiveTab('reports')} style={activeTab === 'reports' ? activeTabBtn : tabBtn}>
            Reports 🚩 {reports.filter(r => r.status === 'pending').length > 0 && <span style={notifBadge}>{reports.filter(r => r.status === 'pending').length}</span>}
          </button>
        )}
      </div>

      {/* Content */}
      <div style={content}>
        {activeTab === 'overview' && (
          <div>
            <p style={desc}>{group.description}</p>
            <div style={{ marginTop: '20px' }}>
              <ReadingHeatmap data={heatmap} title="Shared Growth" />
            </div>
          </div>
        )}

        {activeTab === 'chat' && (
          <GuildMessageBoard 
            groupId={groupId} 
            role={role} 
            user={user} 
            profile={profile}
            group={group}
            members={members} 
            roleNames={roleNames} 
            hasPermission={hasPermission}
            onRefresh={fetchGroupData}
          />
        )}

        {activeTab === 'lists' && (
          <div style={grid}>
            {['owner', 'admin', 'moderator'].includes(role) && (
              <button onClick={() => setIsListModalOpen(true)} style={{ ...secondaryBtn, marginBottom: '10px' }}>
                <Plus size={16} /> Establish New Shelf
              </button>
            )}
            {lists.map(list => (
              <div key={list.id} style={card}>
                <h4 style={{ margin: '0 0 10px 0' }}>{list.name}</h4>
                <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '10px' }}>
                  {list.group_list_items.map(item => (
                    <div key={item.id} style={miniBookContainer}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: 'var(--text-main)', textAlign: 'center' }}>{item.books.title}</span>
                      <button 
                        onClick={() => {
                          const existing = buddySessions.find(s => s.book_id === item.book_id);
                          if (existing) {
                            setSelectedBuddySessionId(existing.id);
                          } else {
                            handleStartBuddySession(item.book_id);
                          }
                        }}
                        style={miniBuddyBtn}
                      >
                        {buddySessions.some(s => s.book_id === item.book_id) ? 'Enter Buddy 👥' : 'Start Buddy 👥'}
                      </button>
                    </div>
                  ))}
                </div>
                {list.group_list_items.length === 0 && <p style={msg}>No books on this shelf yet.</p>}
              </div>
            ))}
            {lists.length === 0 && <p style={msg}>No established shelves yet.</p>}
          </div>
        )}

        {activeTab === 'polls' && (
          <div style={grid}>
            {['owner', 'admin', 'moderator'].includes(role) && (
              <button onClick={() => setIsPollModalOpen(true)} style={{ ...secondaryBtn, marginBottom: '10px' }}>
                <Plus size={16} /> Start New Poll
              </button>
            )}
            {polls.map(poll => (
              <div key={poll.id} style={card}>
                <h4 style={{ margin: '0 0 10px 0' }}>{poll.title}</h4>
                <div style={optionsList}>
                  {poll.group_poll_options.map(opt => (
                    <button key={opt.id} onClick={() => handleVote(poll.id, opt.id)} style={optionBtn}>
                      <span>{opt.title}</span>
                      <span style={voteCount}>{opt.votes_count}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {polls.length === 0 && <p style={msg}>No active polls. Why not suggest a new book?</p>}
          </div>
        )}

        {activeTab === 'members' && (
          <div style={grid}>
            {members.map(m => {
              const canKickThisOne = hasPermission('can_kick_members') && 
                m.user_id !== user.id && 
                m.role !== 'owner' && 
                !(role === 'moderator' && m.role === 'admin') && 
                !(role === 'moderator' && m.role === 'moderator') && 
                !(role === 'admin' && m.role === 'admin');

              const isUserAnon = m.is_anonymous || false;
              let displayName = m.username || 'Unknown';
              let displayAvatar = m.username?.[0] || 'U';

              if (isUserAnon && m.user_id !== user.id && !profile?.is_system_admin) {
                const isViewerOfficer = ['owner', 'admin', 'moderator'].includes(role);
                if (group.allow_officer_anonymity || !isViewerOfficer) {
                  displayName = `Reader #${m.user_id.substring(0, 4)}`;
                  displayAvatar = '🕵️';
                } else {
                  displayName = `${m.username} [Anon to Members]`;
                }
              } else if (isUserAnon && m.user_id === user.id) {
                displayName = `${m.username} (You, Anonymous)`;
              }

              return (
                <div key={m.user_id} style={memberCard}>
                  <div style={avatarSmall}>{displayAvatar}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{displayName}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-soft)' }}>{roleNames[m.role] || m.role}</div>
                  </div>
                  {canKickThisOne && (
                    <button 
                      onClick={async () => {
                        if (!window.confirm(`Are you sure you want to remove ${displayName} from this circle?`)) return;
                        try {
                          const { error } = await supabase.rpc('kick_group_member', {
                            target_group_id: groupId,
                            target_user_id: m.user_id
                          });
                          if (error) throw error;
                          alert("Member removed successfully.");
                          fetchGroupData();
                        } catch (err) {
                          alert("Error removing member: " + err.message);
                        }
                      }}
                      style={{ padding: '6px', borderRadius: '8px', border: 'none', backgroundColor: 'rgba(255, 82, 82, 0.1)', color: '#ff5252', cursor: 'pointer' }}
                      title="Remove Member"
                    >
                      <UserMinus size={16} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'favorites' && (
          <SanctuaryFavorites guildId={groupId} />
        )}

        {activeTab === 'settings' && hasPermission('can_change_settings') && (
          <div>
            {/* Applications */}
            {hasPermission('can_approve_applications') && (
              <div style={{ marginBottom: '2rem' }}>
                <h4 style={sectionLabel}>Pending Applications</h4>
                <div style={grid}>
                  {applications.map(app => (
                    <div key={app.id} style={memberCard}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold' }}>{app.profiles.username}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-soft)' }}>"{app.message}"</div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => handleApplication(app.id, 'approved')} style={approveBtn}><CheckCircle2 size={18} /></button>
                        <button onClick={() => handleApplication(app.id, 'rejected')} style={rejectBtn}><XCircle size={18} /></button>
                      </div>
                    </div>
                  ))}
                  {applications.length === 0 && <p style={msg}>No pending applications.</p>}
                </div>
              </div>
            )}

            {/* Dynamic Custom Permissions Grid */}
            {role === 'owner' && (
              <div style={{ marginBottom: '2rem' }}>
                <h4 style={sectionLabel}>Custom Circle Permissions</h4>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-soft)', marginBottom: '10px' }}>
                  Decide which roles can perform specific leadership duties. Owners always have full access.
                </p>
                <div style={{ backgroundColor: 'var(--bg-app)', padding: '15px', borderRadius: '16px', border: '1px solid var(--border)', overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', textAlign: 'left', minWidth: '400px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        <th style={{ padding: '8px', color: 'var(--text-main)' }}>Action / Capability</th>
                        <th style={{ padding: '8px', textAlign: 'center', color: 'var(--text-main)' }}>Admin</th>
                        <th style={{ padding: '8px', textAlign: 'center', color: 'var(--text-main)' }}>Moderator</th>
                        <th style={{ padding: '8px', textAlign: 'center', color: 'var(--text-main)' }}>Member</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { key: 'can_post_frozen_chat', label: 'Post in Frozen Chat ❄' },
                        { key: 'can_approve_applications', label: 'Approve Applications 📜' },
                        { key: 'can_prune_members', label: 'Prune Inactive Members 🍂' },
                        { key: 'can_change_settings', label: 'Change Group Settings ⚙' },
                        { key: 'can_kick_members', label: 'Kick Members 🛡' },
                        { key: 'can_delete_messages', label: 'Delete Messages 💬' },
                        { key: 'can_view_reports', label: 'Moderate Reports 🚩' }
                      ].map(perm => {
                        const rolesAllowed = group.permissions?.[perm.key] || [];
                        return (
                          <tr key={perm.key} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '8px', fontWeight: 'bold', color: 'var(--text-main)' }}>{perm.label}</td>
                            {['admin', 'moderator', 'member'].map(r => {
                              const checked = rolesAllowed.includes(r);
                              return (
                                <td key={r} style={{ padding: '8px', textAlign: 'center' }}>
                                  <input 
                                    type="checkbox" 
                                    checked={checked}
                                    onChange={async (e) => {
                                      let nextAllowed = [...rolesAllowed];
                                      if (e.target.checked) {
                                        if (!nextAllowed.includes(r)) nextAllowed.push(r);
                                      } else {
                                        nextAllowed = nextAllowed.filter(x => x !== r);
                                      }
                                      if (!nextAllowed.includes('owner')) nextAllowed.push('owner');

                                      const nextPerms = { ...group.permissions, [perm.key]: nextAllowed };
                                      setGroup(prev => ({ ...prev, permissions: nextPerms }));
                                      await supabase.from('book_groups').update({ permissions: nextPerms }).eq('id', groupId);
                                    }}
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Group Options Toggles */}
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={sectionLabel}>Sanctuary Rules & Visibility Settings</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '15px', backgroundColor: 'var(--bg-app)', borderRadius: '16px', border: '1px solid var(--border)' }}>
                
                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '0.85rem', color: 'var(--text-main)' }}>❄ Chat Freeze</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-soft)' }}>Freeze the message board. Only permitted roles will be able to talk.</div>
                  </div>
                  <input 
                    type="checkbox" 
                    checked={!group.chat_enabled} 
                    onChange={async (e) => {
                      const val = !e.target.checked;
                      setGroup(prev => ({ ...prev, chat_enabled: val }));
                      await supabase.from('book_groups').update({ chat_enabled: val }).eq('id', groupId);
                    }}
                  />
                </label>

                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '0.85rem', color: 'var(--text-main)' }}>🕵 Shield Member Anonymity from Officers</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-soft)' }}>Hides incognito member usernames even from Group Admins/Mods (only system staff can see).</div>
                  </div>
                  <input 
                    type="checkbox" 
                    checked={group.allow_officer_anonymity} 
                    onChange={async (e) => {
                      const val = e.target.checked;
                      setGroup(prev => ({ ...prev, allow_officer_anonymity: val }));
                      await supabase.from('book_groups').update({ allow_officer_anonymity: val }).eq('id', groupId);
                    }}
                  />
                </label>

                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '0.85rem', color: 'var(--text-main)' }}>📜 Open Circle Applications</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-soft)' }}>Allow new members to send applications to join this circle.</div>
                  </div>
                  <input 
                    type="checkbox" 
                    checked={group.accepting_applications} 
                    onChange={async (e) => {
                      const val = e.target.checked;
                      setGroup(prev => ({ ...prev, accepting_applications: val }));
                      await supabase.from('book_groups').update({ accepting_applications: val }).eq('id', groupId);
                    }}
                  />
                </label>

                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '0.85rem', color: 'var(--text-main)' }}>🔒 Private (Hidden Search)</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-soft)' }}>Hide this circle from public listings. Members can still find it and join via invite link.</div>
                  </div>
                  <input 
                    type="checkbox" 
                    checked={group.is_private} 
                    onChange={async (e) => {
                      const val = e.target.checked;
                      setGroup(prev => ({ ...prev, is_private: val }));
                      await supabase.from('book_groups').update({ is_private: val }).eq('id', groupId);
                    }}
                  />
                </label>

              </div>
            </div>

            {/* Inactive Pruning */}
            {hasPermission('can_prune_members') && (
              <div style={{ marginBottom: '2rem' }}>
                <h4 style={sectionLabel}>Prune Inactive Members 🍂</h4>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-soft)', marginBottom: '10px' }}>
                  Quickly remove members who haven't logged any message or reading updates for a long period. Leadership roles are never pruned.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', padding: '15px', backgroundColor: 'var(--bg-app)', borderRadius: '16px', border: '1px solid var(--border)' }}>
                  <div style={{ flex: 1 }}>
                    <label style={label}>Inactivity Duration</label>
                    <select 
                      value={pruneDays} 
                      onChange={(e) => setPruneDays(Number(e.target.value))}
                      style={input}
                    >
                      <option value={14}>14 Days</option>
                      <option value={30}>30 Days</option>
                      <option value={60}>60 Days</option>
                      <option value={90}>90 Days</option>
                    </select>
                  </div>
                  <button 
                    onClick={handlePrune} 
                    style={{ ...secondaryBtn, alignSelf: 'flex-end', backgroundColor: 'rgba(255, 82, 82, 0.1)', color: '#ff5252', borderColor: '#ff5252' }}
                  >
                    Prune Roster
                  </button>
                </div>
              </div>
            )}

            {/* Role Config */}
            {role === 'owner' && (
              <div style={{ marginBottom: '2rem' }}>
                <h4 style={sectionLabel}>Custom Role Names</h4>
                <div style={grid}>
                  {Object.entries(roleNames).map(([key, value]) => (
                    <div key={key} style={field}>
                      <label style={label}>{key}</label>
                      <input 
                        value={value} 
                        style={input} 
                        onChange={async (e) => {
                          const next = { ...roleNames, [key]: e.target.value };
                          setGroup(prev => ({ ...prev, role_names: next }));
                          await supabase.from('book_groups').update({ role_names: next }).eq('id', groupId);
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Discord Integration */}
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={sectionLabel}>Discord Integration</h4>
              <div style={field}>
                <label style={label}>Server Link (Discord URL)</label>
                <input 
                  placeholder="https://discord.gg/..." 
                  value={group.discord_server_url || ''} 
                  style={input} 
                  onChange={async (e) => {
                    const val = e.target.value;
                    setGroup(prev => ({ ...prev, discord_server_url: val }));
                    await supabase.from('book_groups').update({ discord_server_url: val }).eq('id', groupId);
                  }}
                />
              </div>
            </div>

            {/* Invite Link */}
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={sectionLabel}>Invite Link</h4>
              <div style={field}>
                <button 
                  style={secondaryBtn}
                  onClick={() => {
                    const link = `${window.location.origin}/join/${groupId}`;
                    navigator.clipboard.writeText(link);
                    alert("Invite link copied to clipboard!");
                  }}
                >
                  Copy Group Invite Link
                </button>
              </div>
            </div>

            {/* Danger Zone */}
            <div>
              <h4 style={{ ...sectionLabel, color: '#ff5252' }}>Sanctuary Exit</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button onClick={handleLeaveGroup} style={secondaryBtn}>
                  Leave Sanctuary
                </button>
                {['owner', 'admin'].includes(role) && (
                  <button onClick={handleArchiveGroup} style={{ ...secondaryBtn, color: '#ff5252', borderColor: '#ff5252' }}>
                    Archive Sanctuary (Admin)
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'reports' && hasPermission('can_view_reports') && (
          <div>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '1.1rem', color: 'var(--text-main)' }}>Sanctuary Scroll Reports</h3>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-soft)', marginBottom: '15px' }}>
              Review scroll messages reported by circle members. Immutably snapshotted at the time of report to ensure concrete context.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {reports.map(rep => {
                const reporterName = members.find(m => m.user_id === rep.reporter_id)?.username || `User #${rep.reporter_id.substring(0, 4)}`;
                const reportedName = members.find(m => m.user_id === rep.reported_user_id)?.username || `User #${rep.reported_user_id.substring(0, 4)}`;

                return (
                  <div key={rep.id} style={{ ...card, opacity: rep.status === 'pending' ? 1 : 0.6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid var(--border)', paddingBottom: '8px', marginBottom: '8px' }}>
                      <div>
                        <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'var(--text-main)' }}>
                          Reported: <span style={{ color: 'var(--accent)' }}>{reportedName}</span>
                        </div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-soft)' }}>
                          Filed by: {reporterName} • {new Date(rep.created_at).toLocaleString()}
                        </div>
                      </div>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '6px',
                        fontSize: '0.65rem',
                        fontWeight: 'bold',
                        color: rep.status === 'pending' ? '#ff5252' : '#4caf50',
                        backgroundColor: rep.status === 'pending' ? 'rgba(255, 82, 82, 0.1)' : 'rgba(76, 175, 80, 0.1)'
                      }}>
                        {rep.status.toUpperCase()}
                      </span>
                    </div>

                    <div style={{ margin: '10px 0', padding: '10px', backgroundColor: 'var(--bg-app)', borderRadius: '10px', fontSize: '0.85rem', borderLeft: '3px solid var(--accent)', color: 'var(--text-main)' }}>
                      <div style={{ fontSize: '0.65rem', color: 'var(--text-soft)', marginBottom: '4px' }}>IMMUTABLE SNAPSHOT ({new Date(rep.context_timestamp).toLocaleTimeString()}):</div>
                      <div>"{rep.context_message}"</div>
                    </div>

                    <div style={{ fontSize: '0.75rem', margin: '5px 0 10px 0', color: 'var(--text-main)' }}>
                      <strong>Reason:</strong> "{rep.reason}"
                    </div>

                    {rep.status === 'pending' && (
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button 
                          onClick={async () => {
                            const { error } = await supabase.from('group_reports').update({ status: 'resolved' }).eq('id', rep.id);
                            if (error) alert(error.message);
                            else fetchGroupData();
                          }}
                          style={{ ...secondaryBtn, padding: '4px 10px', fontSize: '0.7rem', backgroundColor: 'rgba(76, 175, 80, 0.1)', color: '#4caf50', borderColor: '#4caf50' }}
                        >
                          Resolve ✅
                        </button>
                        <button 
                          onClick={async () => {
                            const { error } = await supabase.from('group_reports').update({ status: 'dismissed' }).eq('id', rep.id);
                            if (error) alert(error.message);
                            else fetchGroupData();
                          }}
                          style={{ ...secondaryBtn, padding: '4px 10px', fontSize: '0.7rem', backgroundColor: 'rgba(255, 82, 82, 0.1)', color: '#ff5252', borderColor: '#ff5252' }}
                        >
                          Dismiss ❌
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              {reports.length === 0 && <p style={msg}>No reports filed in this sanctuary.</p>}
            </div>
          </div>
        )}
      </div>

      {/* MODALS */}
      {isPollModalOpen && (
        <CreationModal 
          title="Start a Guild Poll" 
          onClose={() => setIsPollModalOpen(false)}
          onSubmit={(data) => createPoll(data.title, data.options)}
          fields={[
            { id: 'title', label: 'Poll Question', type: 'text', placeholder: 'What should we read next?' },
            { id: 'options', label: 'Options (Comma separated)', type: 'text', placeholder: 'Book A, Book B, Book C' }
          ]}
        />
      )}

      {isListModalOpen && (
        <CreationModal 
          title="Establish a Shelf" 
          onClose={() => setIsListModalOpen(false)}
          onSubmit={(data) => createList(data.name)}
          fields={[{ id: 'name', label: 'Shelf Name', type: 'text', placeholder: 'e.g. Current Reads' }]}
        />
      )}

    </div>
  );
};

// Generic Creation Modal
const CreationModal = ({ title, onClose, onSubmit, fields }) => {
  const [data, setData] = useState({});
  return (
    <div style={modalOverlay}>
      <div style={modalContent}>
        <div style={modalHeader}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={closeBtn}><XCircle /></button>
        </div>
        <form style={{ padding: '20px' }} onSubmit={(e) => {
          e.preventDefault();
          if (data.options) {
            onSubmit({ ...data, options: data.options.split(',').map(s => s.trim()) });
          } else {
            onSubmit(data);
          }
        }}>
          {fields.map(f => (
            <div key={f.id} style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 'bold', color: 'var(--text-soft)', marginBottom: '5px' }}>{f.label}</label>
              <input 
                required
                type={f.type} 
                placeholder={f.placeholder}
                style={input}
                onChange={(e) => setData(prev => ({ ...prev, [f.id]: e.target.value }))}
              />
            </div>
          ))}
          <button type="submit" style={submitBtn}>Create</button>
        </form>
      </div>
    </div>
  );
};

// Styles
const header = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', padding: '10px 0' };
const headerActions = { display: 'flex', gap: '10px' };
const badge = { display: 'inline-block', padding: '2px 10px', backgroundColor: 'var(--accent-soft)', color: 'var(--accent)', borderRadius: '12px', fontSize: '0.7rem', fontWeight: 'bold', marginTop: '5px', textTransform: 'uppercase' };
const nav = { display: 'flex', gap: '8px', borderBottom: '1px solid var(--border)', paddingBottom: '15px', marginBottom: '20px', overflowX: 'auto' };
const tabBtn = { padding: '8px 16px', borderRadius: '10px', border: 'none', background: 'none', color: 'var(--text-soft)', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold', whiteSpace: 'nowrap' };
const activeTabBtn = { ...tabBtn, backgroundColor: 'var(--bg-card)', color: 'var(--accent)', border: '1px solid var(--border)' };
const content = { padding: '5px 0' };
const desc = { fontSize: '0.9rem', color: 'var(--text-soft)', marginBottom: '1.5rem', lineHeight: '1.5' };
const grid = { display: 'flex', flexDirection: 'column', gap: '12px' };
const card = { padding: '15px', backgroundColor: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--border)' };
const memberCard = { ...card, display: 'flex', alignItems: 'center', gap: '12px' };
const avatarSmall = { width: '32px', height: '32px', borderRadius: '50%', backgroundColor: 'var(--accent)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 'bold' };
const miniBook = { flexShrink: 0, width: '80px' };
const miniCover = { width: '80px', height: '120px', borderRadius: '8px', objectFit: 'cover', border: '1px solid var(--border)' };
const miniTitle = { fontSize: '0.65rem', fontWeight: 'bold', color: 'var(--text-main)', marginTop: '5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const optionsList = { display: 'flex', flexDirection: 'column', gap: '8px' };
const optionBtn = { display: 'flex', justifyContent: 'space-between', padding: '10px 15px', borderRadius: '10px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-app)', cursor: 'pointer', fontSize: '0.9rem', color: 'var(--text-main)' };
const voteCount = { fontWeight: 'bold', color: 'var(--accent)' };
const discordBtn = { display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', backgroundColor: '#5865F2', color: 'white', border: 'none', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'none' };
const backBtn = { padding: '8px 16px', backgroundColor: 'var(--bg-app)', color: 'var(--text-soft)', border: '1px solid var(--border)', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.8rem' };
const msg = { textAlign: 'center', padding: '3rem', color: 'var(--text-soft)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' };
const notifBadge = { backgroundColor: '#ff5252', color: 'white', borderRadius: '50%', padding: '2px 6px', fontSize: '0.6rem', marginLeft: '5px' };
const sectionLabel = { fontSize: '0.75rem', fontWeight: 'bold', color: 'var(--text-soft)', textTransform: 'uppercase', marginBottom: '12px', marginTop: '20px' };
const approveBtn = { background: 'none', border: 'none', color: '#4caf50', cursor: 'pointer' };
const rejectBtn = { background: 'none', border: 'none', color: '#ff5252', cursor: 'pointer' };
const closeBtn = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-soft)' };

const modalOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5000, padding: '20px' };
const modalContent = { backgroundColor: 'var(--bg-card)', width: '100%', maxWidth: '400px', borderRadius: '24px', display: 'flex', flexDirection: 'column', maxHeight: '80vh' };
const modalHeader = { padding: '20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const submitBtn = { width: '100%', padding: '12px', backgroundColor: 'var(--accent)', color: 'white', border: 'none', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer' };

const field = { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '15px' };
const label = { fontSize: '0.75rem', fontWeight: 'bold', color: 'var(--text-soft)', textTransform: 'uppercase' };
const input = { padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-app)', color: 'var(--text-main)', fontSize: '0.9rem' };
const secondaryBtn = { padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-card)', color: 'var(--text-main)', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.85rem', width: '100%' };

// Buddy Session Hub Component
const BuddySessionHub = ({ session, onBack }) => {
  const { user } = useAuth();
  const [participants, setParticipants] = useState(session.buddy_participants || []);
  const [myPage, setMyPage] = useState('');
  const [myReady, setMyReady] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setParticipants(session.buddy_participants || []);
    const myInfo = session.buddy_participants?.find(p => p.user_id === user.id);
    if (myInfo) {
      setMyPage(myInfo.current_page.toString());
      setMyReady(myInfo.is_ready);
    }

    const sub = supabase
      .channel(`buddy_session:${session.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'buddy_participants',
        filter: `session_id=eq.${session.id}`
      }, () => {
        fetchParticipants();
      })
      .subscribe();

    return () => {
      sub.unsubscribe();
    };
  }, [session, user]);

  const fetchParticipants = async () => {
    const { data } = await supabase
      .from('buddy_participants')
      .select('*')
      .eq('session_id', session.id);
    if (data) setParticipants(await attachProfiles(data));
  };

  const handleJoin = async () => {
    setLoading(true);
    const { error } = await supabase
      .from('buddy_participants')
      .insert([{ session_id: session.id, user_id: user.id, current_page: 0, is_ready: true }]);
    setLoading(false);
    if (error) {
      window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: error.message, type: 'error' } }));
    } else {
      window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: 'You joined the buddy reading! 👥', type: 'success' } }));
      fetchParticipants();
    }
  };

  const handleUpdate = async () => {
    if (myPage === '') return;
    const pageVal = parseInt(myPage);
    if (isNaN(pageVal) || pageVal < 0) return alert("Please enter a valid page number.");

    setLoading(true);
    const { error } = await supabase
      .from('buddy_participants')
      .update({ current_page: pageVal, is_ready: myReady })
      .eq('session_id', session.id)
      .eq('user_id', user.id);
    setLoading(false);

    if (error) {
      window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: error.message, type: 'error' } }));
    } else {
      window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: 'Progress updated! 📖', type: 'success' } }));
      fetchParticipants();
    }
  };

  const handleLeave = async () => {
    if (!window.confirm("Leave this buddy reading session?")) return;
    setLoading(true);
    const { error } = await supabase
      .from('buddy_participants')
      .delete()
      .eq('session_id', session.id)
      .eq('user_id', user.id);
    setLoading(false);

    if (error) {
      window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: error.message, type: 'error' } }));
    } else {
      window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: 'You left the buddy session.', type: 'info' } }));
      onBack();
    }
  };

  const isJoined = participants.some(p => p.user_id === user.id);

  return (
    <div style={buddyHubContainer}>
      <div style={buddyHubHeader}>
        <button onClick={onBack} style={backBtn}>◀ Back to Circle</button>
        {isJoined && (
          <button onClick={handleLeave} style={leaveSessionBtn}>Leave Session</button>
        )}
      </div>

      <div style={buddyBookCard}>
        <img src={session.books.cover} alt={session.books.title} style={buddyBookCover} />
        <div>
          <h3 style={{ margin: 0, color: 'var(--text-main)', fontSize: '1.1rem' }}>{session.books.title}</h3>
          <p style={{ margin: '4px 0', fontSize: '0.8rem', color: 'var(--text-soft)' }}>by {session.books.author}</p>
          <span style={buddyStatusBadge}>Buddy Reading Active</span>
        </div>
      </div>

      <h4 style={buddySectionLabel}>Participants Progress</h4>
      <div style={buddyParticipantsList}>
        {participants.map(p => (
          <div key={p.user_id} style={participantProgressCard}>
            <div style={participantHeader}>
              <div style={avatarSmall}>{p.profiles?.username?.[0] || 'U'}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{p.profiles?.username}</div>
                <div style={{ fontSize: '0.65rem', color: p.is_ready ? '#4caf50' : 'var(--text-soft)', fontWeight: 'bold' }}>
                  {p.is_ready ? '🟢 Active Reader' : '💤 Paused'}
                </div>
              </div>
              <div style={pageLabel}>Page {p.current_page}</div>
            </div>
            
            <div style={buddyProgressBarBg}>
              <div 
                style={{ 
                  ...buddyProgressBarFill, 
                  width: `${Math.min(100, (p.current_page / (session.books.pages || 300)) * 100)}%` 
                }} 
              />
            </div>
          </div>
        ))}
      </div>

      {isJoined ? (
        <div style={myProgressWidget}>
          <h4 style={myProgressTitle}>Update My Progress</h4>
          <div style={widgetRow}>
            <div style={widgetField}>
              <label style={widgetLabel}>Current Page</label>
              <input 
                type="number" 
                value={myPage} 
                onChange={(e) => setMyPage(e.target.value)} 
                style={input} 
                placeholder="e.g. 150"
              />
            </div>
            <div style={widgetField}>
              <label style={widgetLabel}>Status</label>
              <button 
                onClick={() => setMyReady(!myReady)} 
                style={myReady ? activeReadyBtn : inactiveReadyBtn}
              >
                {myReady ? '🟢 Reading Now' : '💤 Paused'}
              </button>
            </div>
          </div>
          <button onClick={handleUpdate} disabled={loading} style={saveProgressBtn}>
            {loading ? 'Saving...' : 'Save & Broadcast Progress'}
          </button>
        </div>
      ) : (
        <div style={joinPromoCard}>
          <p style={{ margin: '0 0 15px 0', fontSize: '0.85rem', color: 'var(--text-soft)' }}>
            Join your friends and log your reading pages together in real-time!
          </p>
          <button onClick={handleJoin} disabled={loading} style={submitBtn}>
            {loading ? 'Joining...' : 'Join Buddy Reading'}
          </button>
        </div>
      )}
    </div>
  );
};

// Buddy Hub Specific Styles
const buddyHubContainer = { padding: '5px 0' };
const buddyHubHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' };
const buddyBookCard = { display: 'flex', gap: '20px', padding: '15px', backgroundColor: 'var(--bg-card)', borderRadius: '24px', border: '1px solid var(--border)', marginBottom: '1.5rem', alignItems: 'center' };
const buddyBookCover = { width: '60px', height: '90px', borderRadius: '8px', objectFit: 'cover', border: '1px solid var(--border)' };
const buddyStatusBadge = { display: 'inline-block', padding: '2px 8px', backgroundColor: 'var(--accent-soft)', color: 'var(--accent)', borderRadius: '8px', fontSize: '0.65rem', fontWeight: 'bold', marginTop: '6px', textTransform: 'uppercase' };
const buddySectionLabel = { fontSize: '0.75rem', fontWeight: 'bold', color: 'var(--text-soft)', textTransform: 'uppercase', marginBottom: '12px' };
const buddyParticipantsList = { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '1.5rem' };
const participantProgressCard = { padding: '12px', backgroundColor: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--border)' };
const participantHeader = { display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px' };
const pageLabel = { fontSize: '0.75rem', fontWeight: 'bold', color: 'var(--accent)' };
const buddyProgressBarBg = { width: '100%', height: '5px', backgroundColor: 'var(--bg-app)', borderRadius: '2.5px', overflow: 'hidden', marginTop: '5px' };
const buddyProgressBarFill = { height: '100%', backgroundColor: 'var(--accent)', borderRadius: '2.5px', transition: 'width 0.4s ease-in-out' };

const myProgressWidget = { padding: '15px', backgroundColor: 'var(--bg-card)', borderRadius: '24px', border: '1px solid var(--border)' };
const myProgressTitle = { margin: '0 0 12px 0', fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text-main)' };
const widgetRow = { display: 'flex', gap: '12px', marginBottom: '12px' };
const widgetField = { flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' };
const widgetLabel = { fontSize: '0.65rem', fontWeight: 'bold', color: 'var(--text-soft)', textTransform: 'uppercase' };
const inactiveReadyBtn = { padding: '10px', borderRadius: '12px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-app)', color: 'var(--text-soft)', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.8rem' };
const activeReadyBtn = { ...inactiveReadyBtn, backgroundColor: 'rgba(76, 175, 80, 0.1)', color: '#4caf50', borderColor: '#4caf50' };
const saveProgressBtn = { width: '100%', padding: '12px', backgroundColor: 'var(--accent)', color: 'white', border: 'none', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.85rem' };
const leaveSessionBtn = { padding: '6px 12px', backgroundColor: 'none', border: '1px solid #ff5252', color: '#ff5252', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.75rem' };
const joinPromoCard = { padding: '15px', backgroundColor: 'var(--bg-card)', borderRadius: '24px', border: '1px solid var(--border)', textAlign: 'center' };

const miniBookContainer = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', flexShrink: 0, width: '80px' };
const miniBuddyBtn = { width: '100%', padding: '4px 0', borderRadius: '8px', border: 'none', backgroundColor: 'var(--accent-soft)', color: 'var(--accent)', fontSize: '0.65rem', fontWeight: 'bold', cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s' };

// Guild Message Board Component (Lightweight, Secure HTTP Polling Model)
const GuildMessageBoard = ({ groupId, role, user, profile, group, members, roleNames, hasPermission, onRefresh }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  // Reporting State
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportingMsgId, setReportingMsgId] = useState(null);
  const [reportReason, setReportReason] = useState('Spam/Harassment');

  const fetchMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('group_messages')
        .select('*')
        .eq('group_id', groupId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setMessages((await attachProfiles(data)).reverse());
    } catch (err) {
      console.error('Error fetching group messages:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
  }, [groupId]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const { error } = await supabase
        .from('group_messages')
        .insert([{
          group_id: groupId,
          user_id: user.id,
          content: newMessage.trim()
        }]);

      if (error) throw error;
      setNewMessage('');
      await fetchMessages();
    } catch (err) {
      alert(err.message);
    } finally {
      setSending(false);
    }
  };

  const toggleAnonymity = async (newVal) => {
    const { error } = await supabase
      .from('group_members')
      .update({ is_anonymous: newVal })
      .eq('group_id', groupId)
      .eq('user_id', user.id);
    if (error) {
      alert("Failed to toggle anonymity: " + error.message);
    } else {
      if (onRefresh) onRefresh();
    }
  };

  const handleSendReport = async (e) => {
    e.preventDefault();
    if (!reportingMsgId) return;

    try {
      const { error } = await supabase.rpc('submit_group_report', {
        target_group_id: groupId,
        target_message_id: reportingMsgId,
        reason: reportReason
      });

      if (error) throw error;
      alert("🚩 Report sent successfully to leadership. Thank you for keeping the sanctuary safe!");
      setIsReportModalOpen(false);
      setReportingMsgId(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      alert("Error submitting report: " + err.message);
    }
  };

  const getMemberRoleBadge = (msgUserId) => {
    const member = members.find(m => m.user_id === msgUserId);
    if (!member) return null;
    const translatedRole = roleNames[member.role] || member.role;
    
    let badgeColor = 'var(--text-soft)';
    let badgeBg = 'var(--bg-app)';
    if (member.role === 'owner') {
      badgeColor = '#c5a059';
      badgeBg = 'rgba(197, 160, 89, 0.1)';
    } else if (member.role === 'admin') {
      badgeColor = '#a875a6';
      badgeBg = 'rgba(168, 117, 166, 0.1)';
    } else if (member.role === 'moderator') {
      badgeColor = '#72a884';
      badgeBg = 'rgba(114, 168, 132, 0.1)';
    } else {
      badgeColor = 'var(--text-soft)';
      badgeBg = 'var(--bg-app)';
    }

    return (
      <span style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '8px',
        fontSize: '0.65rem',
        fontWeight: 'bold',
        marginLeft: '8px',
        color: badgeColor,
        backgroundColor: badgeBg,
        verticalAlign: 'middle',
        textTransform: 'uppercase',
        border: `1px solid ${badgeColor}`
      }}>
        {translatedRole}
      </span>
    );
  };

  const myMember = members.find(m => m.user_id === user.id);
  const isAnon = myMember?.is_anonymous || false;
  const isChatFrozen = !group?.chat_enabled;
  const canPost = !isChatFrozen || hasPermission('can_post_frozen_chat');

  return (
    <div style={chatContainer}>
      <div style={chatHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MessageSquare size={18} style={{ color: 'var(--accent)' }} />
          <h3 style={{ margin: 0, fontSize: '0.95rem', color: 'var(--text-main)' }}>Sanctuary Scroll Board</h3>
        </div>
        <button onClick={fetchMessages} style={refreshBtn}>
          🔄 Refresh
        </button>
      </div>

      <div style={messageLog}>
        {loading ? (
          <div style={chatMsg}><Loader2 className="spin" /> Reading the scrolls...</div>
        ) : messages.length === 0 ? (
          <div style={chatMsg}>No messages etched on the board yet. Start the conversation!</div>
        ) : (
          messages.map(msg => {
            const isMe = msg.user_id === user.id;
            const senderMember = members.find(m => m.user_id === msg.user_id);
            const isSenderAnon = senderMember?.is_anonymous || false;
            let displayName = msg.profiles?.username || 'Unknown Sanctuary Member';
            let displayAvatar = msg.profiles?.username?.[0] || 'U';

            if (isSenderAnon) {
              if (isMe) {
                displayName = `${msg.profiles?.username || 'Unknown'} (You, Anonymous)`;
              } else if (profile?.is_system_admin) {
                displayName = `${msg.profiles?.username || 'Unknown'} [Incognito]`;
              } else {
                const isViewerOfficer = ['owner', 'admin', 'moderator'].includes(role);
                if (group.allow_officer_anonymity || !isViewerOfficer) {
                  displayName = `Reader #${msg.user_id.substring(0, 4)}`;
                  displayAvatar = '🕵️';
                } else {
                  displayName = `${msg.profiles?.username || 'Unknown'} [Anon to Members]`;
                }
              }
            }

            return (
              <div key={msg.id} style={isMe ? myMessageWrapper : otherMessageWrapper}>
                <div style={messageBubbleHeader}>
                  <span style={messageSender}>{displayName}</span>
                  {getMemberRoleBadge(msg.user_id)}
                  <span style={messageTime}>
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {!isMe && (
                    <button 
                      onClick={() => {
                        setReportingMsgId(msg.id);
                        setReportReason('Spam/Harassment');
                        setIsReportModalOpen(true);
                      }}
                      style={{ background: 'none', border: 'none', color: 'var(--text-soft)', cursor: 'pointer', fontSize: '0.65rem', marginLeft: '10px', display: 'flex', alignItems: 'center', gap: '2px' }}
                    >
                      <Flag size={10} /> Report
                    </button>
                  )}
                </div>
                <div style={isMe ? myMessageBubble : otherMessageBubble}>
                  {msg.content}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Dynamic Chat Freeze Footer */}
      {!canPost ? (
        <div style={{ padding: '15px', backgroundColor: 'var(--bg-app)', borderTop: '1px solid var(--border)', textAlign: 'center', fontSize: '0.85rem', color: 'var(--text-soft)', fontStyle: 'italic', fontWeight: 'bold' }}>
          Scroll Board is currently frozen by sanctuary leadership ❄️
        </div>
      ) : (
        <div style={{ borderTop: '1px solid var(--border)', backgroundColor: 'var(--bg-card)' }}>
          {/* Anonymity Checkbox */}
          <div style={{ padding: '8px 12px 0 12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: 'var(--text-soft)', cursor: 'pointer', width: 'fit-content' }}>
              <input 
                type="checkbox" 
                checked={isAnon} 
                onChange={(e) => toggleAnonymity(e.target.checked)} 
              />
              🕵 Stay Anonymous (Incognito) - Posts as Reader #{user.id.substring(0, 4)}
            </label>
          </div>
          
          <form onSubmit={handleSendMessage} style={chatInputForm}>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Etch a message to the sanctuary..."
              style={chatInput}
              maxLength={1000}
            />
            <button type="submit" disabled={sending || !newMessage.trim()} style={chatSendBtn}>
              {sending ? '...' : 'Send'}
            </button>
          </form>
        </div>
      )}

      {/* Snapshot Report Modal */}
      {isReportModalOpen && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <div style={modalHeader}>
              <h3 style={{ margin: 0, fontSize: '0.95rem' }}>🚩 Report Scroll Entry</h3>
              <button onClick={() => setIsReportModalOpen(false)} style={closeBtn}><XCircle size={18} /></button>
            </div>
            <form onSubmit={handleSendReport} style={{ padding: '20px' }}>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-soft)', marginBottom: '15px' }}>
                This will file a permanent timestamped snapshot of this message for sanctuary moderation.
              </p>
              <div style={field}>
                <label style={label}>Reason for Report</label>
                <select 
                  value={reportReason} 
                  onChange={(e) => setReportReason(e.target.value)}
                  style={input}
                >
                  <option value="Spam/Harassment">Spam/Harassment</option>
                  <option value="Inappropriate Content">Inappropriate Content</option>
                  <option value="Spoilers without tags">Spoilers without tags</option>
                  <option value="Other Rule Violation">Other Rule Violation</option>
                </select>
              </div>
              <button type="submit" style={submitBtn}>File Report</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Sage/Cozy styled parameters for the Chat System
const chatContainer = {
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'var(--bg-card)',
  borderRadius: '24px',
  border: '1px solid var(--border)',
  height: '500px',
  overflow: 'hidden',
  marginBottom: '20px'
};

const chatHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 20px',
  borderBottom: '1px solid var(--border)',
  backgroundColor: 'var(--bg-app)',
  opacity: 0.9
};

const refreshBtn = {
  padding: '6px 12px',
  fontSize: '0.75rem',
  backgroundColor: 'var(--bg-card)',
  color: 'var(--text-soft)',
  border: '1px solid var(--border)',
  borderRadius: '10px',
  cursor: 'pointer',
  fontWeight: 'bold',
  transition: 'all 0.2s'
};

const messageLog = {
  flex: 1,
  padding: '20px',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  backgroundColor: 'var(--bg-app)'
};

const chatMsg = {
  textAlign: 'center',
  padding: '3rem',
  color: 'var(--text-soft)',
  fontSize: '0.85rem'
};

const messageBubbleHeader = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: '4px',
  fontSize: '0.75rem',
  width: '100%'
};

const messageSender = {
  fontWeight: 'bold',
  color: 'var(--text-main)'
};

const messageTime = {
  marginLeft: '10px',
  color: 'var(--text-soft)',
  fontSize: '0.65rem'
};

const myMessageWrapper = {
  alignSelf: 'flex-end',
  maxWidth: '80%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end'
};

const otherMessageWrapper = {
  alignSelf: 'flex-start',
  maxWidth: '80%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start'
};

const messageBubble = {
  padding: '12px 16px',
  borderRadius: '20px',
  fontSize: '0.85rem',
  lineHeight: '1.5',
  wordBreak: 'break-word'
};

const myMessageBubble = {
  ...messageBubble,
  backgroundColor: 'var(--accent)',
  color: 'white',
  borderBottomRightRadius: '4px'
};

const otherMessageBubble = {
  ...messageBubble,
  backgroundColor: 'var(--bg-card)',
  color: 'var(--text-main)',
  border: '1px solid var(--border)',
  borderBottomLeftRadius: '4px'
};

const chatInputForm = {
  display: 'flex',
  padding: '12px',
  borderTop: '1px solid var(--border)',
  backgroundColor: 'var(--bg-card)',
  gap: '10px'
};

const chatInput = {
  flex: 1,
  padding: '12px 16px',
  borderRadius: '14px',
  border: '1px solid var(--border)',
  backgroundColor: 'var(--bg-app)',
  color: 'var(--text-main)',
  fontSize: '0.85rem'
};

const chatSendBtn = {
  padding: '12px 20px',
  borderRadius: '14px',
  border: 'none',
  backgroundColor: 'var(--accent)',
  color: 'white',
  fontWeight: 'bold',
  cursor: 'pointer',
  fontSize: '0.85rem'
};

export default GroupHub;
